import { Note, Folder, Tag, AppSettings, defaultSettings } from '../types';

export class RealStorageService {
  private readonly STORAGE_KEYS = {
    NOTES: 'suishouji_notes',
    FOLDERS: 'suishouji_folders', 
    TAGS: 'suishouji_tags',
    SETTINGS: 'suishouji_settings',
    BACKUP: 'suishouji_backup',
    VERSION: 'suishouji_version'
  };

  private readonly CURRENT_VERSION = '1.0.0';

  // 检查存储可用性
  private isStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  // 检测移动设备
  private isMobileDevice(): boolean {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768 && window.innerHeight <= 1024);
  }

  // 安全的JSON解析
  private safeJsonParse<T>(data: string | null, defaultValue: T): T {
    if (!data) return defaultValue;
    try {
      return JSON.parse(data);
    } catch (error) {
      console.warn('JSON解析失败:', error);
      return defaultValue;
    }
  }

  // 安全的存储操作
  private safeSetItem(key: string, value: any): boolean {
    if (!this.isStorageAvailable()) {
      console.error('localStorage不可用');
      return false;
    }

    try {
      const jsonString = JSON.stringify(value);
      
      // 检查数据大小（localStorage通常限制5-10MB）
      const dataSize = new Blob([jsonString]).size;
      if (dataSize > 4 * 1024 * 1024) { // 4MB警告
        console.warn(`数据量较大 (${Math.round(dataSize / 1024)}KB)，可能影响性能`);
      }

      localStorage.setItem(key, jsonString);
      
      // 移动设备额外保存备份
      if (this.isMobileDevice() && key !== this.STORAGE_KEYS.BACKUP) {
        localStorage.setItem(this.STORAGE_KEYS.BACKUP, jsonString);
      }
      
      return true;
    } catch (error) {
      console.error(`存储失败 (${key}):`, error);
      
      // 尝试清理存储空间
      if (error instanceof Error && error.name === 'QuotaExceededError') {
        this.cleanupStorage();
        // 再次尝试
        try {
          localStorage.setItem(key, JSON.stringify(value));
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  }

  // 清理存储空间
  private cleanupStorage(): void {
    try {
      // 移除备份数据（如果存在）
      localStorage.removeItem(this.STORAGE_KEYS.BACKUP);
      console.log('已清理备份数据以释放存储空间');
    } catch (error) {
      console.error('清理存储空间失败:', error);
    }
  }

  // 数据迁移
  private migrateData(): void {
    const currentVersion = localStorage.getItem(this.STORAGE_KEYS.VERSION);
    if (currentVersion !== this.CURRENT_VERSION) {
      console.log(`数据版本升级: ${currentVersion} -> ${this.CURRENT_VERSION}`);
      localStorage.setItem(this.STORAGE_KEYS.VERSION, this.CURRENT_VERSION);
    }
  }

  // 笔记操作
  saveNotes(notes: Note[]): boolean {
    return this.safeSetItem(this.STORAGE_KEYS.NOTES, notes);
  }

  loadNotes(): Note[] {
    this.migrateData();
    const data = localStorage.getItem(this.STORAGE_KEYS.NOTES);
    const notes = this.safeJsonParse<Note[]>(data, []);
    // 将字符串日期转换为Date对象
    return notes.map(note => ({
      ...note,
      createdAt: new Date(note.createdAt),
      updatedAt: new Date(note.updatedAt),
    }));
  }

  // 文件夹操作
  saveFolders(folders: Folder[]): boolean {
    return this.safeSetItem(this.STORAGE_KEYS.FOLDERS, folders);
  }

  loadFolders(): Folder[] {
    const data = localStorage.getItem(this.STORAGE_KEYS.FOLDERS);
    const folders = this.safeJsonParse<Folder[]>(data, []);
    // 将字符串日期转换为Date对象
    return folders.map(folder => ({
      ...folder,
      createdAt: new Date(folder.createdAt),
      updatedAt: new Date(folder.updatedAt),
    }));
  }

  // 标签操作
  saveTags(tags: Tag[]): boolean {
    return this.safeSetItem(this.STORAGE_KEYS.TAGS, tags);
  }

  loadTags(): Tag[] {
    const data = localStorage.getItem(this.STORAGE_KEYS.TAGS);
    return this.safeJsonParse(data, []);
  }

  // 设置操作
  saveSettings(settings: AppSettings): boolean {
    return this.safeSetItem(this.STORAGE_KEYS.SETTINGS, settings);
  }

  loadSettings(): AppSettings {
    const data = localStorage.getItem(this.STORAGE_KEYS.SETTINGS);
    return this.safeJsonParse(data, defaultSettings);
  }

  // 完整数据备份
  saveAllData(data: {
    notes: Note[];
    folders: Folder[];
    tags: Tag[];
    settings: AppSettings;
  }): boolean {
    const success = [
      this.saveNotes(data.notes),
      this.saveFolders(data.folders),
      this.saveTags(data.tags),
      this.saveSettings(data.settings)
    ].every(result => result);

    if (success) {
      // 保存完整备份
      const backupData = {
        ...data,
        timestamp: new Date().toISOString(),
        version: this.CURRENT_VERSION
      };
      this.safeSetItem(this.STORAGE_KEYS.BACKUP, backupData);
    }

    return success;
  }

  // 数据恢复
  loadAllData(): {
    notes: Note[];
    folders: Folder[];
    tags: Tag[];
    settings: AppSettings;
  } {
    return {
      notes: this.loadNotes(),
      folders: this.loadFolders(),
      tags: this.loadTags(),
      settings: this.loadSettings()
    };
  }

  // 清除所有数据
  clearAllData(): void {
    Object.values(this.STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  // 获取存储使用情况
  getStorageInfo(): {
    used: number;
    available: number;
    percentage: number;
  } {
    if (!this.isStorageAvailable()) {
      return { used: 0, available: 0, percentage: 0 };
    }

    let used = 0;
    Object.values(this.STORAGE_KEYS).forEach(key => {
      const data = localStorage.getItem(key);
      if (data) {
        used += new Blob([data]).size;
      }
    });

    // localStorage通常限制5-10MB，这里假设5MB
    const available = 5 * 1024 * 1024;
    const percentage = (used / available) * 100;

    return {
      used: Math.round(used / 1024), // KB
      available: Math.round(available / 1024), // KB
      percentage: Math.round(percentage)
    };
  }

  // 导出数据（用于备份）
  exportToFile(): string {
    const data = this.loadAllData();
    return JSON.stringify({
      ...data,
      exportTime: new Date().toISOString(),
      version: this.CURRENT_VERSION
    }, null, 2);
  }

  // 从文件导入数据
  importFromFile(jsonString: string): boolean {
    try {
      const data = JSON.parse(jsonString);
      
      // 验证数据格式
      if (!data.notes || !Array.isArray(data.notes)) {
        throw new Error('无效的数据格式');
      }

      return this.saveAllData({
        notes: data.notes || [],
        folders: data.folders || [],
        tags: data.tags || [],
        settings: { ...defaultSettings, ...data.settings }
      });
    } catch (error) {
      console.error('导入数据失败:', error);
      return false;
    }
  }
}

// 创建单例实例
const realStorageService = new RealStorageService();
export default realStorageService;